import { createTheme, Direction, Shadows, Theme } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { DRAWER_WIDTH } from '../ui/TitledDrawer';
import { mb } from './mbTokens';
import { CustomTheme } from './types';

declare module '@mui/material/styles' {
  interface Palette {
    mb: typeof mb;
  }
  interface PaletteOptions {
    mb?: typeof mb;
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
  }
}

const FONT_FAMILY = "'Roboto', 'Heebo', system-ui, sans-serif";

export const createMbTheme = (direction: Direction, customTheme: CustomTheme): Theme => {
  const shadows = [...createTheme().shadows] as Shadows;
  shadows[1] = mb.cardShadow;
  shadows[2] = mb.cardShadow;
  shadows[3] = mb.cardShadow;
  return createTheme({
    direction,
    customTheme,
    palette: {
      primary: { main: mb.green, dark: mb.greenHover, light: mb.mint, contrastText: '#fff' },
      secondary: { main: mb.clay, dark: mb.clayInk, light: mb.claySoft, contrastText: mb.ink },
      success: { main: mb.green, dark: mb.greenHover, light: mb.mint, contrastText: '#fff' },
      info: { main: mb.green, dark: mb.greenHover, light: mb.mint, contrastText: '#fff' },
      warning: { main: mb.highlight, dark: mb.clayInk, light: mb.claySoft, contrastText: mb.ink },
      error: { main: mb.danger, light: mb.dangerSoft, contrastText: '#fff' },
      background: { default: mb.ground, paper: mb.surface },
      text: { primary: mb.ink, secondary: mb.muted, disabled: 'rgba(31, 26, 23, 0.42)' },
      divider: mb.line,
      action: {
        active: mb.muted,
        hover: 'rgba(31, 26, 23, 0.04)',
        selected: 'rgba(19, 86, 53, 0.08)',
        focus: 'rgba(19, 86, 53, 0.12)',
      },
      mb,
    },
    shape: { borderRadius: mb.radius.control },
    shadows,
    typography: {
      fontFamily: FONT_FAMILY,
      h1: { fontSize: 30, fontWeight: 700, lineHeight: 1.2 },
      h2: { fontSize: 26, fontWeight: 700, lineHeight: 1.2 },
      h3: { fontSize: 24, fontWeight: 700, lineHeight: 1.1 },
      h4: { fontSize: 21, fontWeight: 700, lineHeight: 1.25 },
      h5: { fontSize: 19, fontWeight: 700, lineHeight: 1.25 },
      h6: { fontSize: 17, fontWeight: 600, lineHeight: 1.3 },
      subtitle1: { fontSize: 16, fontWeight: 500 },
      subtitle2: { fontSize: 15, fontWeight: 600 },
      body1: { fontSize: 15, fontWeight: 400, lineHeight: 1.5 },
      body2: { fontSize: 14, fontWeight: 400, lineHeight: 1.45 },
      caption: { fontSize: 13, fontWeight: 400 },
      button: { fontSize: 15, fontWeight: 500, textTransform: 'none' },
      overline: { fontSize: 12, fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiButtonBase: { styleOverrides: { root: { fontFamily: FONT_FAMILY } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: mb.radius.control,
            minHeight: 44,
            paddingInline: 16,
            fontWeight: 500,
            textTransform: 'none',
          },
          sizeSmall: { minHeight: 40, paddingInline: 12, fontSize: 14 },
          sizeLarge: { minHeight: 48, paddingInline: 20, fontSize: 16 },
          startIcon: { marginInlineStart: -4, marginInlineEnd: 8 },
          endIcon: { marginInlineEnd: -4, marginInlineStart: 8 },
        },
        variants: [
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              fontWeight: 600,
              '&:hover': { backgroundColor: mb.greenHover },
              '&.Mui-disabled': { backgroundColor: mb.disabledFill, color: mb.muted },
            },
          },
          {
            props: { variant: 'soft' },
            style: {
              backgroundColor: mb.surface,
              color: mb.green,
              fontWeight: 600,
              border: `1px solid ${mb.green}`,
              '&:hover': { backgroundColor: mb.ground },
              '&.Mui-disabled': { backgroundColor: mb.surface, color: mb.muted, borderColor: mb.control },
            },
          },
          {
            props: { variant: 'outlined', color: 'primary' },
            style: {
              backgroundColor: mb.surface,
              color: mb.green,
              fontWeight: 600,
              border: `1px solid ${mb.green}`,
              '&:hover': { backgroundColor: mb.ground, border: `1px solid ${mb.green}` },
              '&.Mui-disabled': { backgroundColor: mb.surface, color: mb.muted, borderColor: mb.control },
            },
          },
          {
            props: { variant: 'outlined', color: 'inherit' },
            style: {
              backgroundColor: mb.surface,
              color: mb.ink,
              fontWeight: 500,
              border: `1px solid ${mb.control}`,
              '&:hover': { backgroundColor: mb.ground, border: `1px solid ${mb.control}` },
              '&.Mui-disabled': { backgroundColor: mb.surface, color: mb.muted, borderColor: mb.control },
            },
          },
          {
            props: { variant: 'outlined', color: 'error' },
            style: { backgroundColor: mb.surface, fontWeight: 600, '&:hover': { backgroundColor: mb.dangerSoft } },
          },
          { props: { variant: 'text' }, style: { '&:hover': { backgroundColor: mb.ground } } },
        ],
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: mb.radius.control, '&:hover': { backgroundColor: mb.ground } },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: mb.radius.control,
            border: `1px solid ${mb.control}`,
            backgroundColor: mb.surface,
            color: mb.ink,
            textTransform: 'none',
            minHeight: 44,
            paddingInline: 16,
            fontWeight: 500,
            '&:hover': { backgroundColor: mb.ground },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: mb.ink,
              color: mb.surface,
              borderColor: mb.ink,
              fontWeight: 600,
            },
          },
          sizeSmall: { minHeight: 40, paddingInline: 12 },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { backgroundColor: mb.surface, borderRadius: mb.radius.control },
          grouped: {
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: mb.mint,
              color: mb.green,
              borderColor: mb.control,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: mb.radius.card },
          outlined: { borderColor: mb.line },
          root: { backgroundImage: 'none' },
          elevation1: { border: `1px solid ${mb.line}` },
          elevation2: { border: `1px solid ${mb.line}` },
          elevation3: { border: `1px solid ${mb.line}` },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: mb.radius.chip, fontWeight: 500 },
          sizeSmall: { height: 24, fontSize: 13 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: mb.radius.control,
            backgroundColor: mb.surface,
            minHeight: 44,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: mb.control },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: mb.muted },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: mb.green, borderWidth: 2 },
            '&.Mui-disabled': { backgroundColor: mb.ground },
          },
          input: { paddingBlock: 10.5 },
          inputSizeSmall: { paddingBlock: 8.5 },
          sizeSmall: { minHeight: 40 },
          multiline: { paddingBlock: 10.5 },
          inputMultiline: { paddingBlock: 0 },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { color: mb.muted, '&.Mui-focused:not(.Mui-error)': { color: mb.green } },
          outlined: {
            transform: 'translate(14px, 11px) scale(1)',
            '&.MuiInputLabel-sizeSmall': { transform: 'translate(14px, 9px) scale(1)' },
            '&.MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
          },
        },
      },
      MuiFormHelperText: { styleOverrides: { root: { color: mb.muted, marginInline: 0 } } },
      MuiSelect: { styleOverrides: { icon: { color: mb.muted } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: mb.radius.card, border: `1px solid ${mb.line}` } } },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 40,
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: mb.mint, color: mb.green },
          },
        },
      },
      MuiPopover: { styleOverrides: { paper: { borderRadius: mb.radius.card } } },
      MuiAutocomplete: {
        styleOverrides: {
          // MUI pads the input root with 9px (and the input with 7.5px), which stands about 55px tall; this keeps
          // the 44px (40px small) control height and leaves the inline padding alone.
          root: {
            '& .MuiOutlinedInput-root': { paddingBlock: 2 },
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': { paddingBlock: 5.5 },
          },
          paper: {
            borderRadius: mb.radius.card,
            border: `1px solid ${mb.line}`,
            boxShadow: '0 8px 24px rgba(31, 26, 23, 0.12)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            // A dialog opened from a side drawer (e.g. add payment on an order) centers in the space beside the drawer
            // and leaves it undimmed, so the details it was opened from stay readable. The drawer's own backdrop
            // already dims the rest of the page. Physical `right` is flipped by the RTL plugin, like the drawer anchor.
            '@media (min-width: 1200px)': {
              'body:has(.titled-drawer) &': {
                '& .MuiDialog-container': { paddingRight: DRAWER_WIDTH },
                '& .MuiBackdrop-root': { backgroundColor: 'transparent' },
              },
            },
          },
          paper: { borderRadius: mb.radius.card, backgroundColor: mb.surface },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: 21, fontWeight: 700, padding: '24px 24px 16px' } } },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            // The outlined labels sit 9px above their field, so the first one needs 12px of room not to be clipped.
            padding: '12px 24px 16px',
            '.MuiDialogTitle-root + &': { paddingTop: 12 },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '16px 24px', gap: 8, '& > :not(:first-of-type)': { marginLeft: 0 } },
        },
      },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: mb.surface } } },
      MuiBackdrop: { styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: mb.backdrop } } } },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: mb.surface,
            color: mb.ink,
            boxShadow: 'none',
            borderBottom: `1px solid ${mb.lineSoft}`,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: mb.surface,
            border: `1px solid ${mb.line}`,
            boxShadow: 'none',
            borderRadius: mb.radius.card,
            '&:before': { display: 'none' },
            '&, &.Mui-expanded': { margin: 0 },
            '& + .MuiAccordion-root, & + .MuiAccordion-root.Mui-expanded': { marginTop: 12 },
            '&:first-of-type': { borderRadius: mb.radius.card },
            '&:last-of-type': { borderRadius: mb.radius.card },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: { minHeight: 0, padding: '14px 16px', '&.Mui-expanded': { minHeight: 0 } },
          content: { margin: 0, '&.Mui-expanded': { margin: 0 } },
          expandIconWrapper: { color: mb.muted },
        },
      },
      MuiAccordionDetails: { styleOverrides: { root: { padding: '0 16px 16px' } } },
      MuiSlider: {
        styleOverrides: {
          rail: { backgroundColor: mb.lineSoft, opacity: 1 },
          track: { backgroundColor: mb.green, border: 'none' },
          thumb: {
            width: 20,
            height: 20,
            borderRadius: mb.radius.control,
            backgroundColor: mb.green,
            boxShadow: '0 0 0 5px rgba(19, 86, 53, 0.14)',
            '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 8px rgba(19, 86, 53, 0.18)' },
          },
          markLabel: { fontSize: 13, color: mb.muted },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            minHeight: 42,
            borderRadius: mb.radius.control,
            '&:hover': { backgroundColor: mb.ground },
            '&.Mui-focusVisible': { outline: `2px solid ${mb.green}`, outlineOffset: -2 },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: mb.green,
              color: '#fff',
              fontWeight: 600,
              '--mb-icon-wash': mb.iconWashActive,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { display: 'none' },
          flexContainer: { gap: 4 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 15,
            minHeight: 40,
            paddingInline: 14,
            borderRadius: mb.radius.control,
            '&:hover': { backgroundColor: mb.ground },
            '&.Mui-selected': { color: mb.green, backgroundColor: mb.mint },
          },
          textColorPrimary: { color: mb.ink },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${mb.lineSoft}` },
          head: { color: mb.muted, fontWeight: 600, fontSize: 13, borderBottom: `1px solid ${mb.line}` },
          stickyHeader: { backgroundColor: mb.surface },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mb.ink,
            color: mb.surface,
            borderRadius: mb.radius.control,
            fontSize: 13,
            fontWeight: 500,
          },
          arrow: { color: mb.ink },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: mb.radius.control, '& .MuiAlert-icon': { color: 'inherit' } },
          standardSuccess: { backgroundColor: mb.mint, color: mb.green },
          standardInfo: { backgroundColor: mb.ground, color: mb.ink, border: `1px solid ${mb.line}` },
          standardWarning: { backgroundColor: mb.claySoft, color: mb.clayInk },
          standardError: { backgroundColor: mb.dangerSoft, color: mb.danger },
        },
      },
      MuiAvatar: { styleOverrides: { root: { borderRadius: mb.radius.control } } },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: 'rgba(31, 26, 23, 0.08)' } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: mb.radius.chip, backgroundColor: mb.lineSoft } } },
      MuiBadge: { styleOverrides: { badge: { borderRadius: mb.radius.chip } } },
      MuiFab: { styleOverrides: { root: { borderRadius: mb.radius.card, boxShadow: mb.cardShadow } } },
      MuiPickersDay: { styleOverrides: { root: { borderRadius: mb.radius.control } } },
    },
  });
};
